import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth } from './contexts/AuthContext';
import { createMvkgTheme } from './theme/mvkgTheme';

// Pages. Login/Landing stay eager (the signed-out entry points); every other page
// is its own chunk, so the first paint no longer parses the graph renderer, the
// PDF viewer and every admin card (one 5 MB main bundle) before showing Home.
import Landing from './pages/Landing';
import Login from './pages/Login';

// Components
import Layout from './components/Layout';

// KaTeX CSS for LaTeX rendering
import 'katex/dist/katex.min.css';

const Articles = lazy(() => import('./pages/Articles'));
const GraphVisualization = lazy(() => import('./pages/GraphVisualization'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const PeopleAuthorProfile = lazy(() => import('./pages/PeopleAuthorProfile'));
const PeopleCoReaderProfile = lazy(() => import('./pages/PeopleCoReaderProfile'));
const RagQuery = lazy(() => import('./pages/RagQuery'));
const AuthorInsights = lazy(() => import('./pages/AuthorInsights'));
const Profile = lazy(() => import('./pages/Profile'));
const ArticleDigest = lazy(() => import('./pages/ArticleDigest')); // OWNER: A8
const DirectionMap = lazy(() => import('./pages/DirectionMap')); // OWNER: A6
const Discovery = lazy(() => import('./pages/Discovery'));   // H.1
const EntityHub = lazy(() => import('./pages/EntityHub'));
const Home = lazy(() => import('./pages/Home'));

const theme = createMvkgTheme();

function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <div>Loading...</div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Suspense fallback={null}>
        <Routes>
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/" /> : <Login />} 
          />
          {/* Single signed-in home. `/` and `/dashboard` are the SAME page (Home,
              the user's reading hub); both render it so old /dashboard links keep
              working without a redirect that can race the router. The corpus
              snapshot that used to be Home stays at /workspace. */}
          <Route
            path="/"
            element={
              isAuthenticated ? <Layout><Home /></Layout> : <Landing />
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? <Layout><Home /></Layout> : <Navigate to="/login" />
            }
          />
          <Route 
            path="/articles" 
            element={
              isAuthenticated ? <Layout><Articles /></Layout> : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/graph" 
            element={
              isAuthenticated ? <Layout><GraphVisualization /></Layout> : <Navigate to="/login" />
            } 
          />
          <Route
            path="/author-insights"
            element={
              isAuthenticated ? <Layout><AuthorInsights /></Layout> : <Navigate to="/login" />
            }
          />
          <Route path="/rag" element={
            isAuthenticated ? <Layout><RagQuery /></Layout> : <Navigate to="/login" />
          } />
          <Route path="/rag/:sessionId" element={
            isAuthenticated ? <Layout><RagQuery /></Layout> : <Navigate to="/login" />
          } />
          <Route path="/profile" element={
            isAuthenticated ? <Layout><Profile /></Layout> : <Navigate to="/login" />
          } />
          <Route path="/admin"
            element={
              isAuthenticated ? <Layout><Admin /></Layout> : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/events"
            element={
              isAuthenticated ? <Layout><AdminEvents /></Layout> : <Navigate to="/login" />
            }
          />
          <Route
            path="/author-insights/author/:id"
            element={
              isAuthenticated ? <Layout><PeopleAuthorProfile /></Layout> : <Navigate to="/login" />
            }
          />
          <Route
            path="/author-insights/co-reader/:id"
            element={
              isAuthenticated ? <Layout><PeopleCoReaderProfile /></Layout> : <Navigate to="/login" />
            }
          />
          {/* OWNER: A8 — Article Digest route */}
          <Route
            path="/digest/:articleId"
            element={
              isAuthenticated ? <Layout><ArticleDigest /></Layout> : <Navigate to="/login" />
            }
          />
          {/* OWNER: A6 — Direction Map route */}
          <Route
            path="/directions"
            element={
              isAuthenticated ? <Layout><DirectionMap /></Layout> : <Navigate to="/login" />
            }
          />
          {/* H.1 — Discovery dashboard */}
          <Route
            path="/discovery"
            element={
              isAuthenticated ? <Layout><Discovery /></Layout> : <Navigate to="/login" />
            }
          />
          {/* H.2 — URL ingest consolidated into the Articles "From URL" tab.
              Keep /ingest as a redirect so old links/bookmarks still resolve. */}
          <Route path="/ingest" element={<Navigate to="/articles" replace />} />
          {/* H.3 — Method Detail */}
          <Route
            path="/method/:id"
            element={
              isAuthenticated ? <Layout><EntityHub kind="method" /></Layout> : <Navigate to="/login" />
            }
          />
          {/* Entity hub: one reading page for any graph vertex. The older
              per-kind routes above render the same page. */}
          <Route
            path="/entity/:kind/:id"
            element={
              isAuthenticated ? <Layout><EntityHub /></Layout> : <Navigate to="/login" />
            }
          />
          {/* The old corpus dashboard: everything it showed now lives on Home. */}
          <Route path="/workspace" element={<Navigate to="/" replace />} />
          <Route
            path="/dataset/:datasetId"
            element={
              isAuthenticated ? <Layout><EntityHub kind="dataset" /></Layout> : <Navigate to="/login" />
            }
          />
          <Route
            path="/problem/:problemId"
            element={
              isAuthenticated ? <Layout><EntityHub kind="problem" /></Layout> : <Navigate to="/login" />
            }
          />
          <Route
            path="/topic/:topicId"
            element={
              isAuthenticated ? <Layout><EntityHub kind="auto" /></Layout> : <Navigate to="/login" />
            }
          />
          <Route
            path="/direction/:directionId"
            element={
              isAuthenticated ? <Layout><EntityHub kind="direction" /></Layout> : <Navigate to="/login" />
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default App;
